import React, {
  useCallback, useEffect, useState,
} from 'react';

import { IPaginationResponse, ResortStatus } from 'api/resort/types';
import { HeaderClickEvent, IGridColumn, IGridSorting } from 'components/common/Grid/types';

import { WEATHER_PAGE_SIZE } from 'ots-constants';
import { Diagnostics } from 'util/diagnostics';
import { fetchRegionResortsByStatus } from 'api/region/static';

import { GridLoadable } from 'components/common/Grid/GridLoadable';
import { gridColumns } from 'components/region/OpenDatesTable/gridColumns';
import { IRegionResortsPaginated, IResortShortReport } from 'api/region/types';
import { useOTSTranslation } from 'i18n/useOTSTranslation';

const PAGE_RESORTS_STATUS = ResortStatus.openingDates;

export interface IProps {
  list: IRegionResortsPaginated;
  regionId: string;
  hasMore: boolean,
}

export const OpenDatesTable = ({
  hasMore,
  regionId,
  list,
}: IProps) => {
  const { t } = useOTSTranslation('common');
  const [resorts, setResorts] = useState<Array<IResortShortReport>>(list.data);
  const [pagination, setPagination] = useState<IPaginationResponse>(list.pagination);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasLoadMore, setLoadMore] = useState<boolean>(hasMore);
  const [gridSorting, setGridSorting] = useState<IGridSorting>();

  const loadNextPage = useCallback(async () => {
    setIsLoading(true);
    const nextPage = (pagination?.page ?? 0) + 1;

    try {
      const data = await fetchRegionResortsByStatus(
        regionId,
        PAGE_RESORTS_STATUS,
        nextPage,
        WEATHER_PAGE_SIZE,
        gridSorting?.orderBy,
        gridSorting?.direction,
      );

      const updatedResortList = [...resorts, ...data.data];
      setResorts(updatedResortList);
      setPagination(data.pagination);

      setLoadMore(data.pagination.count > updatedResortList.length);
      setIsLoading(false);
    } catch (e: any) {
      Diagnostics.error({
        tag: 'region-resorts',
        error: e,
        message: `Failed to load next page for region ${regionId}`,
        data: { regionId, nextPage },
      });
      setIsLoading(false);
    }
  }, [resorts, pagination, gridSorting]);

  const onSortingChange = useCallback(async (
    event: HeaderClickEvent, column: IGridColumn, sorting?: IGridSorting,
  ) => {
    setIsLoading(true);
    try {
      const response = await fetchRegionResortsByStatus(
        regionId,
        PAGE_RESORTS_STATUS,
        0,
        WEATHER_PAGE_SIZE,
        sorting?.orderBy,
        sorting?.direction,
      );

      setResorts(response.data);
      setPagination(response.pagination);
      setLoadMore(response.pagination.count > response.data.length);
      setGridSorting(sorting);
    } catch (e: any) {
      Diagnostics.error({
        tag: 'region-lift-tickets',
        error: e,
        message: `Failed to load lift-tickets next page for region ${regionId}`,
        data: { regionId, nextPage: pagination?.page },
      });
    }
    setIsLoading(false);
  }, [regionId, pagination]);

  useEffect(() => {
    setResorts(list.data);
    setPagination(list.pagination);
  }, [list]);

  useEffect(() => {
    setLoadMore(hasMore);
  }, [hasMore]);

  return (
    <GridLoadable
      data={resorts}
      columns={gridColumns()}
      setIsLoading={setIsLoading}
      onSortingChange={onSortingChange}
      loadNextPage={loadNextPage}
      isFresh={!isLoading}
      isLoading={isLoading}
      isLoadMoreAvailable={hasLoadMore}
      total={pagination?.count || 0}
      noValueSymbol={t('common.dash')}
    />
  );
};
